import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
  NotificationModel,
} from "services/notification.service";
import { getAccessToken } from "services/token.service";
import {
  getNotificationRows,
  getNotificationTotalCount,
} from "utils/notification";

interface UseNotificationCenterOptions {
  pageSize: number;
  autoLoad?: boolean;
}

interface MarkNotificationAsReadOptions {
  refreshList?: boolean;
}

const mergeNotificationList = (
  currentItems: NotificationModel[],
  nextItems: NotificationModel[],
  append: boolean,
): NotificationModel[] => {
  const mergedItems = append ? [...currentItems, ...nextItems] : nextItems;
  const uniqueMap = new Map<string, NotificationModel>();

  mergedItems.forEach((item) => {
    uniqueMap.set(item.id, item);
  });

  return Array.from(uniqueMap.values());
};

export const useNotificationCenter = ({
  pageSize,
  autoLoad = true,
}: UseNotificationCenterOptions) => {
  const accessToken = getAccessToken() || "";
  const [page, setPage] = useState<number>(1);
  const [notificationList, setNotificationList] = useState<NotificationModel[]>(
    [],
  );
  const [totalNotificationCount, setTotalNotificationCount] = useState<
    number | null
  >(0);
  const [lastLoadedSize, setLastLoadedSize] = useState<number>(0);
  const refreshTimerRef = useRef<number | null>(null);

  const {
    data: unreadNotificationRes,
    isFetching: isFetchingUnreadCount,
    refetch: refetchUnreadCount,
  } = useGetNotificationsQuery(
    {
      query: {
        page: 1,
        limit: 1,
        filter: "status:eq:unread",
      },
    },
    {
      skip: !accessToken,
    },
  );

  const [fetchNotifications, { isFetching: isFetchingNotifications }] =
    useLazyGetNotificationsQuery();

  const [markNotificationAsReadMutation, { isLoading: isMarkingRead }] =
    useMarkNotificationAsReadMutation();

  const [
    markAllNotificationsAsReadMutation,
    { isLoading: isMarkingAllAsRead },
  ] = useMarkAllNotificationsAsReadMutation();

  const unreadCount = useMemo(() => {
    const totalCount = getNotificationTotalCount(unreadNotificationRes);

    if (totalCount !== null) {
      return totalCount;
    }

    return getNotificationRows(unreadNotificationRes).length;
  }, [unreadNotificationRes]);

  const hasMoreNotifications = useMemo(() => {
    if (totalNotificationCount === null) {
      return lastLoadedSize === pageSize;
    }

    return notificationList.length < totalNotificationCount;
  }, [
    lastLoadedSize,
    notificationList.length,
    pageSize,
    totalNotificationCount,
  ]);

  const clearRefreshTimer = useCallback((): void => {
    if (refreshTimerRef.current !== null) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const loadNotificationPage = useCallback(
    async (nextPage: number, append: boolean): Promise<void> => {
      if (!accessToken) {
        setPage(1);
        setNotificationList([]);
        setTotalNotificationCount(0);
        setLastLoadedSize(0);
        return;
      }

      try {
        const response = await fetchNotifications({
          query: {
            page: nextPage,
            limit: pageSize,
          },
        }).unwrap();

        const rows = getNotificationRows(response);
        const count = getNotificationTotalCount(response);

        setPage(nextPage);
        setLastLoadedSize(rows.length);
        setTotalNotificationCount(count);
        setNotificationList((currentItems) =>
          mergeNotificationList(currentItems, rows, append),
        );
      } catch (error) {
        console.error("Fetch notifications failed:", error);
      }
    },
    [accessToken, fetchNotifications, pageSize],
  );

  const refreshNotifications = useCallback(async (): Promise<void> => {
    clearRefreshTimer();

    if (!accessToken) {
      setPage(1);
      setNotificationList([]);
      setTotalNotificationCount(0);
      setLastLoadedSize(0);
      return;
    }

    await loadNotificationPage(1, false);
    void refetchUnreadCount();
  }, [
    accessToken,
    clearRefreshTimer,
    loadNotificationPage,
    refetchUnreadCount,
  ]);

  const scheduleRefreshNotifications = useCallback(
    (delay = 350): void => {
      clearRefreshTimer();

      refreshTimerRef.current = window.setTimeout(() => {
        refreshTimerRef.current = null;
        void refreshNotifications();
      }, delay);
    },
    [clearRefreshTimer, refreshNotifications],
  );

  const markNotificationAsReadById = useCallback(
    async (
      id: string,
      options?: MarkNotificationAsReadOptions,
    ): Promise<void> => {
      await markNotificationAsReadMutation({ id }).unwrap();

      setNotificationList((currentItems) =>
        currentItems.map((item) =>
          item.id === id
            ? {
                ...item,
                status: "read",
                readAt: item.readAt ?? new Date().toISOString(),
              }
            : item,
        ),
      );

      if (options?.refreshList) {
        await refreshNotifications();
        return;
      }

      void refetchUnreadCount();
    },
    [markNotificationAsReadMutation, refreshNotifications, refetchUnreadCount],
  );

  const markAllNotificationsAsRead = useCallback(async (): Promise<void> => {
    await markAllNotificationsAsReadMutation().unwrap();
    await refreshNotifications();
  }, [markAllNotificationsAsReadMutation, refreshNotifications]);

  const loadMoreNotifications = useCallback(async (): Promise<void> => {
    if (isFetchingNotifications || !hasMoreNotifications) {
      return;
    }

    await loadNotificationPage(page + 1, true);
  }, [
    hasMoreNotifications,
    isFetchingNotifications,
    loadNotificationPage,
    page,
  ]);

  useEffect(() => {
    if (!accessToken) {
      clearRefreshTimer();
      setPage(1);
      setNotificationList([]);
      setTotalNotificationCount(0);
      setLastLoadedSize(0);
      return;
    }

    if (autoLoad) {
      void refreshNotifications();
    }
  }, [accessToken, autoLoad, clearRefreshTimer, refreshNotifications]);

  useEffect(() => clearRefreshTimer, [clearRefreshTimer]);

  return {
    accessToken,
    notificationList,
    unreadCount,
    hasMoreNotifications,
    isFetchingNotifications,
    isFetchingUnreadCount,
    isMarkingRead,
    isMarkingAllAsRead,
    refreshNotifications,
    scheduleRefreshNotifications,
    markNotificationAsReadById,
    markAllNotificationsAsRead,
    loadMoreNotifications,
  };
};
